// components/SearchPanel.tsx
import React from 'react';
import {
  Box,
  Button,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  SearchCondition,
  SearchKeyOption,
  KeyValueOptionsMap,
} from '../common/search/types';
import type { SearchValue } from '../common/search/types';

const useStyles = makeStyles({
  row: { marginBottom: 8 },
  actions: { display: 'flex', gap: 8, marginTop: 8 },
});

type Props = {
  conditions: SearchCondition[];
  onChange: (next: SearchCondition[]) => void;
  onSearch: (args: { conditions: SearchCondition[]; pageSize: number }) => void;
  onClear: () => void;
  searchKeys: SearchKeyOption[];
  keyValueOptions?: KeyValueOptionsMap;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: number[];
  allowAddCondition?: boolean;
};

export const SearchPanel: React.FC<Props> = ({
  conditions,
  onChange,
  onSearch,
  onClear,
  searchKeys,
  keyValueOptions = {},
  pageSize,
  onPageSizeChange,
  pageSizeOptions = [10, 50, 100, 500, 1000],
  allowAddCondition = true,
}) => {
  const classes = useStyles();

  const handleConditionChange = (
    index: number,
    field: 'key' | 'value',
    value: SearchValue // ← string 固定をやめる
  ) => {
    const next = [...conditions];
    next[index] = { ...next[index], [field]: value };
    // キー変更時、選択肢→自由入力/その逆が起こるため値はリセット推奨
    if (field === 'key') next[index].value = '';
    onChange(next);
  };

  const addCondition = () => onChange([...conditions, { key: '', value: '' }]);
  const removeCondition = (idx: number) =>
    onChange(conditions.filter((_, i) => i !== idx));

  return (
    <Box mb={2}>
      {conditions.map((c, idx) => {
        const optionsForKey = keyValueOptions[c.key];
        return (
          <Grid
            container
            spacing={2}
            key={idx}
            alignItems="center"
            className={classes.row}
          >
            <Grid item xs={4}>
              <TextField
                select
                fullWidth
                label="Key"
                value={c.key}
                onChange={(e) =>
                  handleConditionChange(idx, 'key', e.target.value)
                }
                variant="outlined"
                size="small"
              >
                {searchKeys.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              {optionsForKey ? (
                <TextField
                  select
                  fullWidth
                  label="Value"
                  value={(c.value ?? '') as any}
                  onChange={(e) =>
                    handleConditionChange(idx, 'value', e.target.value)
                  }
                  variant="outlined"
                  size="small"
                >
                  {optionsForKey.map((opt) => (
                    <MenuItem
                      key={String(opt.value)} // ← Key は string/number 必須
                      value={opt.value as any} // ← boolean もそのまま運ぶ
                    >
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  fullWidth
                  label="Value"
                  value={(c.value as string) ?? ''} // 自由入力は string を想定
                  onChange={(e) =>
                    handleConditionChange(idx, 'value', e.target.value)
                  }
                  variant="outlined"
                  size="small"
                  placeholder="任意の値"
                />
              )}
            </Grid>
            <Grid item xs={2}>
              {allowAddCondition && conditions.length > 1 && (
                <Button
                  onClick={() => removeCondition(idx)}
                  variant="outlined"
                  color="secondary"
                >
                  削除
                </Button>
              )}
            </Grid>
          </Grid>
        );
      })}

      <Box className={classes.actions}>
        {allowAddCondition && (
          <Button onClick={addCondition} variant="contained" color="primary">
            条件を追加
          </Button>
        )}

        <Select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          variant="outlined"
          style={{ width: 160 }}
        >
          {pageSizeOptions.map((n) => (
            <MenuItem key={n} value={n}>
              {n}件/ページ
            </MenuItem>
          ))}
        </Select>

        <Button
          onClick={() => onSearch({ conditions, pageSize })}
          variant="contained"
          color="secondary"
        >
          検索
        </Button>
        <Button onClick={onClear} variant="outlined">
          クリア
        </Button>
      </Box>

      <Box mt={1}>
        <Typography variant="subtitle2">検索条件:</Typography>
        <Box display="flex" flexWrap="wrap" gridGap={12}>
          {conditions.map((c, i) => (
            <Box key={i}>
              <b>Key:</b> {c.key || '未設定'}, <b>Value:</b>{' '}
              {c.value !== '' && c.value !== null && c.value !== undefined
                ? String(c.value) // ← boolean/null も安全に表示
                : '未設定'}
            </Box>
          ))}
          <Box>
            , <b>１ページ当たりの件数:</b> {pageSize}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
